import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, getAuthToken } from "@/lib/api";
import { useSession } from "@/lib/session";
import { LOCATIONS, LOCATIONS_GR } from "@/lib/suppliers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PlayCircle } from "lucide-react";
import { useT } from "@/lib/i18n";

export default function NewLogin() {
  const { setShift } = useSession();
  const nav = useNavigate();
  const t = useT();
  // Tenant-aware locations. Falls back to the original MASTER_JOHN 4 stores
  // for legacy device-locked kiosks that don't carry a JWT.
  const [locations, setLocations] = useState(LOCATIONS);
  const [defaultFloat, setDefaultFloat] = useState("");
  const [form, setForm] = useState({
    employee_name: "",
    location: "",
    register: 0,
    // Iter 22 — Starting Float baseline removed. Default 0.00 for every
    // shift. Operator can still override per-shift if they actually run
    // a non-zero opening drawer.
    starting_float: "0.00",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!getAuthToken()) return; // MASTER_JOHN legacy device — keep defaults
    api.get("/auth/me")
      .then(({ data }) => {
        const tLocs = data?.tenant?.locations || [];
        if (tLocs.length > 0) setLocations(tLocs);
        const f = data?.tenant?.default_float;
        if (f != null) {
          setDefaultFloat(String(f));
          setForm((s) => ({ ...s, starting_float: String(f) }));
        }
      })
      .catch(() => { /* stay on defaults */ });
  }, []);

  const valid =
    form.employee_name.trim() &&
    form.location &&
    (form.register === 1 || form.register === 2) &&
    form.starting_float !== "";

  const submit = async () => {
    if (!valid) {
      toast.error("Please complete every field");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/shifts/start", {
        employee_name: form.employee_name.trim(),
        location: form.location,
        register: form.register,
        starting_float: Number(form.starting_float),
      });
      setShift(data);
      toast.success(`Shift started for ${data.employee_name}`);
      nav("/invoices");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start shift");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <header className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
          {t("newLogin.title")}
        </h1>
        <p className="text-slate-500 mt-1 text-base tracking-wide">
          {t("newLogin.subtitle")}
        </p>
      </header>

      <Card className="p-8 sm:p-10 bg-white border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              {t("newLogin.register")}
            </Label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {[1, 2].map((r) => (
                <button
                  key={r}
                  data-testid={`register-${r}-btn`}
                  onClick={() => setForm((f) => ({ ...f, register: r }))}
                  className={`min-h-[88px] rounded-xl border-2 transition-colors font-bold text-2xl ${
                    form.register === r
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-900 hover:border-slate-400"
                  }`}
                >
                  Register {r}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              {t("newLogin.location")}
            </Label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {locations.map((loc) => (
                <button
                  key={loc}
                  data-testid={`location-${loc.toLowerCase().replace(/\s+/g, "-")}-btn`}
                  onClick={() => setForm((f) => ({ ...f, location: loc }))}
                  className={`min-h-[72px] rounded-xl border-2 transition-colors flex flex-col items-center justify-center px-2 ${
                    form.location === loc
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-900 hover:border-slate-400"
                  }`}
                >
                  <span className="font-semibold text-base text-center">{loc}</span>
                  {LOCATIONS_GR[loc] && (
                    <span className="text-[11px] opacity-70 mt-0.5">{LOCATIONS_GR[loc]}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="name" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              {t("newLogin.employee")}
            </Label>
            <Input
              id="name"
              data-testid="employee-name-input"
              value={form.employee_name}
              onChange={(e) => setForm((f) => ({ ...f, employee_name: e.target.value }))}
              placeholder="e.g. Giannos"
              className="mt-3 min-h-[56px] text-lg font-semibold border-2"
            />
          </div>

          <div>
            <Label htmlFor="float" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              {t("newLogin.starting")}
            </Label>
            <Input
              id="float"
              data-testid="starting-float-input"
              type="number"
              inputMode="decimal"
              step="0.01"
              value={form.starting_float}
              onChange={(e) => setForm((f) => ({ ...f, starting_float: e.target.value }))}
              placeholder="50.00"
              className="mt-3 min-h-[56px] text-2xl font-mono font-bold text-center border-2"
            />
          </div>
        </div>

        <Button
          onClick={submit}
          disabled={!valid || submitting}
          data-testid="start-shift-btn"
          className="mt-10 w-full min-h-[80px] bg-slate-900 hover:bg-slate-800 text-white text-2xl font-bold uppercase tracking-wider gap-3"
        >
          <PlayCircle className="h-7 w-7" />
          {submitting ? "…" : t("common.startShift")}
        </Button>
      </Card>
    </div>
  );
}
